/**
 * useVerificationValidation Hook
 *
 * Consolidates validation logic for verification treatments.
 * Replaces repetitive validation if-statements with data-driven approach.
 */

import { useState, useCallback } from 'react';
import { getStopApi } from '../../../services/StopApi/StopApi';
import { VERIFICATION_BASE_URL } from '../../../constants';
import {
  TREATMENT_TYPES,
  TREATMENT_STATUS_IDS,
  COUNTABLE_STATUS_IDS,
  VALIDATION_MESSAGES,
  TREATMENT_FIELDS,
} from '../utils/verificationConstants';
import {
  getTreatmentFieldConfig,
  calculateUnitCounts,
  formatDateForApi,
} from '../utils/verificationHelpers';
import moment from 'moment';
import { formDateFormat } from '../../../constants';

/**
 * Hook to handle verification validation logic
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.verificationState - Current verification state
 * @param {string} params.authToken - Authorization token
 * @param {Function} params.setVarificationPopUp - Function to show validation popup
 * @param {Function} params.setVarificationMessage - Function to set validation message
 * @param {Date|string} params.monday - Monday date of the week
 * @param {Date|string} params.sunday - Sunday date of the week
 * @returns {Object} Validation functions
 */
const useVerificationValidation = ({
  verificationState,
  authToken,
  setVarificationPopUp,
  setVarificationMessage,
  monday,
  sunday,
}) => {
  const [isValidating, setIsValidating] = useState(false);

  /**
   * Validate P1 - Check authorization, CBP rates, and total units
   */
  const validateP1 = useCallback(async () => {
    try {
      const commonParams = `${VERIFICATION_BASE_URL}/verify/ValidateP1/${verificationState?.verSapAthId}/${verificationState?.verCboId}/${verificationState?.verSapAthMaxUnits}/${verificationState?.verPrtId}/${moment(monday).format('M')}`;
      const authorization = authToken;

      const response = await getStopApi({ commonParams, authorization });
      const validateP1Message = response.data;

      if (validateP1Message?.isSapAthExists?.errorMessage) {
        setVarificationPopUp(true);
        setVarificationMessage(validateP1Message.isSapAthExists.errorMessage);
        return false;
      }

      if (validateP1Message?.cbpRatesCounts?.errorMessage) {
        setVarificationPopUp(true);
        setVarificationMessage(validateP1Message.cbpRatesCounts.errorMessage);
        return false;
      }

      if (validateP1Message?.totalUnits?.errorMessage) {
        setVarificationPopUp(true);
        setVarificationMessage(validateP1Message.totalUnits.errorMessage);
        return false;
      }

      return true;
    } catch (error) {
      alert(error);
      return false;
    }
  }, [
    verificationState,
    authToken,
    monday,
    setVarificationPopUp,
    setVarificationMessage,
  ]);

  /**
   * Validate P2 - Check week date range
   */
  const validateP2 = useCallback(async () => {
    try {
      const endDate = moment(sunday).format(formDateFormat);
      const commonParams = `${VERIFICATION_BASE_URL}/verify/ValidateP2/${verificationState?.verSapAthId}/${monday}/${endDate}`;
      const authorization = authToken;

      const response = await getStopApi({ commonParams, authorization });
      const validateP2Message = response.data;

      if (validateP2Message?.errorMessage) {
        setVarificationPopUp(true);
        setVarificationMessage(validateP2Message.errorMessage);
        return false;
      }

      return true;
    } catch (error) {
      alert(error);
      return false;
    }
  }, [
    verificationState,
    authToken,
    monday,
    sunday,
    setVarificationPopUp,
    setVarificationMessage,
  ]);

  /**
   * Validate P3 - Check treatment status and unit counts
   * @param {string} latestUpdateField - Field name that was updated
   */
  const validateP3 = useCallback(
    async (latestUpdateField) => {
      setIsValidating(true);

      try {
        // Get field configuration
        const fieldConfig = getTreatmentFieldConfig(latestUpdateField);
        if (!fieldConfig) {
          setIsValidating(false);
          return true;
        }

        // Get status ID from state
        const tmtStsId = verificationState[latestUpdateField];
        if (!tmtStsId) {
          setIsValidating(false);
          return true;
        }

        // Call API to validate status
        const commonParams = `${VERIFICATION_BASE_URL}/verify/ValidateP3/${tmtStsId}`;
        const authorization = authToken;
        const response = await getStopApi({ commonParams, authorization });

        // If status is not countable (response.data !== 1), skip unit counting
        if (response?.data !== 1) {
          setIsValidating(false);
          return true;
        }

        // Check if status counts towards limits
        if (!COUNTABLE_STATUS_IDS.includes(parseInt(tmtStsId))) {
          setIsValidating(false);
          return true;
        }

        // Calculate unit counts
        const totalWeekUnits = calculateUnitCounts(verificationState);
        const totalIndividualUnits = calculateUnitCounts(
          verificationState,
          TREATMENT_TYPES.INDIVIDUAL
        );
        const totalGroupUnits = calculateUnitCounts(
          verificationState,
          TREATMENT_TYPES.GROUP
        );

        // Validate against max week units
        if (totalWeekUnits > verificationState?.verSapAthMaxWkUnits) {
          setVarificationPopUp(true);
          setVarificationMessage(VALIDATION_MESSAGES.MAX_WEEK_UNITS);
          setIsValidating(false);
          return false;
        }

        // Validate against max individual units
        if (totalIndividualUnits > verificationState?.verSapAthMaxIndUnits) {
          setVarificationPopUp(true);
          setVarificationMessage(VALIDATION_MESSAGES.MAX_INDIVIDUAL_UNITS);
          setIsValidating(false);
          return false;
        }

        // Validate against max group units
        if (totalGroupUnits > verificationState?.verSapAthMaxGrpUnits) {
          setVarificationPopUp(true);
          setVarificationMessage(VALIDATION_MESSAGES.MAX_GROUP_UNITS);
          setIsValidating(false);
          return false;
        }

        setIsValidating(false);
        return true;
      } catch (error) {
        alert(error);
        setIsValidating(false);
        return false;
      }
    },
    [
      verificationState,
      authToken,
      setVarificationPopUp,
      setVarificationMessage,
    ]
  );

  /**
   * Validate same-day treatment conflict
   * @returns {boolean} True if validation passes (no conflicts)
   */
  const validateSameDayTreatment = useCallback(() => {
    for (let i = 1; i <= 7; i++) {
      const individualField = `i${i}TmtTmtStsId`;
      const groupField = `g${i}TmtTmtStsId`;

      const hasIndividual =
        verificationState[individualField] !== null &&
        verificationState[individualField] !== undefined;
      const hasGroup =
        verificationState[groupField] !== null &&
        verificationState[groupField] !== undefined;

      if (hasIndividual && hasGroup) {
        setVarificationPopUp(true);
        setVarificationMessage(VALIDATION_MESSAGES.SAME_DAY_TREATMENT);
        return false;
      }
    }

    return true;
  }, [verificationState, setVarificationPopUp, setVarificationMessage]);

  /**
   * Run all validations
   * @param {string} latestUpdateField - Latest field that was updated
   * @returns {Promise<boolean>} True if all validations pass
   */
  const validateAll = useCallback(
    async (latestUpdateField) => {
      // Validate same-day treatment first (synchronous, faster)
      if (!validateSameDayTreatment()) {
        return false;
      }

      // Validate P3 if a field was updated
      if (latestUpdateField) {
        const p3Valid = await validateP3(latestUpdateField);
        if (!p3Valid) {
          return false;
        }
      }

      return true;
    },
    [validateSameDayTreatment, validateP3]
  );

  return {
    validateP1,
    validateP2,
    validateP3,
    validateSameDayTreatment,
    validateAll,
    isValidating,
  };
};

export default useVerificationValidation;
